import type { Events } from '|>/shared/events'

/**
 * Utility function to check if the given hotkey is an array format hotkey,
 * like `[string | string[], () => void]`
 */
function isArrayHotkey(x: unknown): x is Events.Controls.ArrayHotkey {
  return Array.isArray(x) && x.length === 2 && typeof x[1] === 'function'
}

/**
 * Utility function to check if the given hotkey is an object format hotkey,
 * like `{ key: string | string[], handler: () => void }`
 */
function isObjectHotkey(x: unknown): x is Events.Controls.ObjectHotkey {
  return (
    x != null &&
    typeof x === 'object' &&
    'key' in x &&
    'handler' in x &&
    typeof x.handler === 'function'
  )
}

/**
 * Utility function to check if the given hotkey is valid
 */
export function isHotkey(x: unknown): x is Events.Controls.Hotkey {
  return isArrayHotkey(x) || isObjectHotkey(x)
}

/**
 * Utility function to convert hotkey description from `Events.Controls.Hotkey`,
 * which can be either in array format `[string | string[], () => void]`
 * or as plain object `{ key: string | string[], handler: () => void }`,
 * to just `[string | string[], () => void]`
 */
export function asArray(
  hotkey: Events.Controls.Hotkey
): Events.Controls.ArrayHotkey {
  if (isArrayHotkey(hotkey)) {
    return hotkey
  }

  if (isObjectHotkey(hotkey)) {
    return [hotkey.key, hotkey.handler]
  }

  // invalid hotkey format
  throw new Error(`Invalid hotkey: ${hotkey}`)
}
